import { DownloadTextFile } from "./utils";
import { exportReportToCsvZip } from "./exportCsvZip";
import { exportReportToExcel } from "./exportExcel";
import { exportReportToPdf } from "./exportPdf";
import { buildExportReportModel } from "./exportReportModel";
import { exportPromptToText } from "./exportPromptText";
import { buildTextExport } from "./exportText";
import { getExportText } from "./exportI18n";

const sanitizeFilenamePart = (value = "") =>
  String(value)
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const buildBaseFilename = ({ filters, reportName, t }) =>
  [
    sanitizeFilenamePart(reportName || t("reports.report", "التقرير")),
    sanitizeFilenamePart(filters?.period || "report"),
    Date.now(),
  ].join("-");

export const OnExportReport = async ({
  getAdvancedReportInfo,
  reportApiResponse,
  selectedReport,
  reportData,
  aiAnalysis,
  filters,
  format,
  isRTL,
  toast,
  t,
}) => {
  const uiLanguage = isRTL ? "ar" : "en";

  if (!selectedReport || !reportData) {
    toast({
      title: getExportText(t, uiLanguage, "reports.exportError", "خطأ في التصدير", "Export error"),
      description: getExportText(
        t,
        uiLanguage,
        "reports.noDataToExport",
        "لا توجد بيانات للتصدير",
        "No data available to export"
      ),
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
    return;
  }

  try {
    const reportName = getAdvancedReportInfo(selectedReport)?.name || t("reports.report", "التقرير");
    const baseFilename = buildBaseFilename({
      filters,
      reportName,
      t,
    });

    if (format === "prompt") {
      exportPromptToText({
        filename: `${baseFilename}-ai-prompt.txt`,
        filters,
        language: uiLanguage,
        reportName,
        t,
      });

      toast({
        title: getExportText(
          t,
          uiLanguage,
          "reports.exportSuccess",
          "تم التصدير",
          "Export successful"
        ),
        description: getExportText(
          t,
          uiLanguage,
          "reports.promptDownloaded",
          "تم تنزيل البرومبت المرسل إلى الذكاء الاصطناعي بصيغة TXT",
          "The AI prompt was downloaded as TXT"
        ),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const reportModel = buildExportReportModel({
      aiAnalysis,
      getAdvancedReportInfo,
      isRTL,
      reportApiResponse,
      reportData,
      selectedReport,
    });
    const reportFilename = buildBaseFilename({
      filters,
      reportName: reportModel.reportName,
      t,
    });

    toast({
      title: getExportText(t, reportModel.language, "reports.exporting", "جاري التصدير...", "Exporting..."),
      description: `${getExportText(
        t,
        reportModel.language,
        "reports.preparingFormat",
        "تحضير",
        "Preparing"
      )} ${format}`,
      status: "info",
      duration: 2000,
      isClosable: true,
    });

    if (format === "text") {
      DownloadTextFile(buildTextExport({ model: reportModel, t }), `${reportFilename}.txt`);
    } else if (format === "pdf") {
      exportReportToPdf({ filename: `${reportFilename}.pdf`, model: reportModel, t });
    } else if (format === "excel") {
      exportReportToExcel({ filename: `${reportFilename}.xlsx`, model: reportModel, t });
    } else if (format === "csv") {
      const totalFiles = await exportReportToCsvZip({
        filename: `${reportFilename}.zip`,
        model: reportModel,
        t,
      });

      if (!totalFiles) {
        toast({
          title: getExportText(
            t,
            reportModel.language,
            "reports.noDataToExport",
            "لا توجد بيانات قابلة للتصدير",
            "No exportable data available"
          ),
          description: getExportText(
            t,
            reportModel.language,
            "reports.csvNoSections",
            "لم يتم إنشاء ملفات CSV لأن الأقسام المعروضة لا تحتوي بيانات فعلية.",
            "No CSV files were generated because the visible sections do not contain actual data."
          ),
          status: "warning",
          duration: 3500,
          isClosable: true,
        });
        return;
      }

      toast({
        title: getExportText(
          t,
          reportModel.language,
          "reports.exportSuccess",
          "تم التصدير",
          "Export successful"
        ),
        description: `${getExportText(
          t,
          reportModel.language,
          "reports.csvZipDownloaded",
          "تم تنزيل ملف ZIP لملفات CSV",
          "CSV ZIP downloaded"
        )} (${totalFiles})`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    toast({
      title: getExportText(
        t,
        reportModel.language,
        "reports.exportSuccess",
        "تم التصدير",
        "Export successful"
      ),
      description: `${getExportText(
        t,
        reportModel.language,
        "reports.reportSavedAs",
        "تم حفظ التقرير كـ",
        "Report saved as"
      )} ${format}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    toast({
      title: getExportText(t, uiLanguage, "reports.exportError", "خطأ في التصدير", "Export error"),
      description:
        error?.message ||
        getExportText(
          t,
          uiLanguage,
          "reports.exportErrorFallback",
          "تعذر تصدير التقرير",
          "Failed to export report"
        ),
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  }
};
